/**
 * The GM's private channel to the showrunner.
 *
 * Two modes, and the distinction is the whole point:
 *
 * - **Steering** changes what the agent prepares next. "Get them to the harbour
 *   tonight" biases future branches.
 * - **By the way** asks a question of campaign memory and changes nothing.
 *   "What do we know about Lady Catrin?"
 *
 * GMs conflated these constantly, so the mode switch carries an explicit
 * explanation rather than relying on the labels.
 *
 * Neither is ever seen by the players.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';

import type { ChatMessage, ChatMode } from '@/api/types';
import { userFacingError } from '@/api/client';

const MODE_COPY: Record<
  ChatMode,
  { help: string; placeholder: string; send: string }
> = {
  steering: {
    help: 'Changes what gets prepared next.',
    placeholder: 'Bring the missing brother into the next scene…',
    send: 'Steer',
  },
  by_the_way: {
    help: 'Asks campaign memory a question. Does not steer the story.',
    placeholder: 'What does Lady Catrin know about the missing ledger?',
    send: 'Ask',
  },
};

interface Props {
  messages: ChatMessage[];
  disabled: boolean;
  onSend: (mode: ChatMode, content: string) => Promise<void>;
  /** Controlled, network-free state for the recorded tutorial. */
  presentation?: { mode: ChatMode; value: string; sending?: boolean };
}

export function ChatPanel({ messages, disabled, onSend, presentation }: Props) {
  const [localMode, setMode] = useState<ChatMode>('steering');
  const mode = presentation?.mode ?? localMode;
  const [localValue, setValue] = useState('');
  const value = presentation?.value ?? localValue;
  const [localSending, setSending] = useState(false);
  const sending = presentation?.sending ?? localSending;
  const [error, setError] = useState<string | null>(null);
  const pinned = useRef(true);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = scroller.current;
    if (node && pinned.current) node.scrollTop = node.scrollHeight;
  }, [messages]);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (presentation) return;
    const content = value.trim();
    if (!content || sending || disabled) return;
    setSending(true);
    setError(null);
    try {
      await onSend(mode, content);
      setValue('');
      pinned.current = true;
    } catch (err) {
      setError(
        userFacingError(
          err,
          'Your message wasn’t sent. It’s saved below; try again.',
        ),
      );
    } finally {
      setSending(false);
    }
  }

  const copy = MODE_COPY[mode];

  return (
    <div className="pane">
      <header className="pane__header">
        <h2 className="pane__title">Your private assistant</h2>
        <div className="segmented" role="group" aria-label="Message type">
          {(['steering', 'by_the_way'] as const).map((m) => (
            <button
              key={m}
              type="button"
              className={
                m === mode ? 'segmented__item is-active' : 'segmented__item'
              }
              aria-pressed={m === mode}
              onClick={() => setMode(m)}
              title={MODE_COPY[m].help}
            >
              {m === 'steering' ? 'Steer' : 'Ask'}
            </button>
          ))}
        </div>
      </header>

      <div
        className="pane__body"
        ref={scroller}
        onScroll={() => {
          const node = scroller.current;
          if (node)
            pinned.current =
              node.scrollHeight - node.scrollTop - node.clientHeight < 48;
        }}
      >
        {messages.length === 0 ? (
          <p className="chat-empty">
            Give the next scene a direction, or ask about anyone in your world.
            This conversation stays between you and Throughline.
          </p>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={
                'chat-msg chat-msg--' +
                (message.role === 'gm' || message.role === 'user'
                  ? 'gm'
                  : 'agent')
              }
            >
              <span className="chat-msg__who">
                {/* The wire says 'user'/'assistant'; the optimistic local copy
                    says 'gm'. Testing only for 'gm' labelled the GM's own
                    message "Showrunner" the moment the server echoed it back —
                    so every steer appeared twice, once as theirs and once as a
                    reply that had never been written. */}
                {message.role === 'gm' || message.role === 'user'
                  ? 'You'
                  : 'Throughline'}
                {message.mode === 'by_the_way' ? ' · question' : ''}
              </span>
              <p className="chat-msg__body">
                {message.pending ? <em>Thinking…</em> : message.content}
              </p>
            </div>
          ))
        )}
      </div>

      <form className="chat-form" onSubmit={submit}>
        {error ? (
          <p className="notice notice--error" role="alert">
            {error}
          </p>
        ) : null}
        {/* Only before the first message. The steer-vs-by-the-way distinction
            is real and GMs did conflate it, but three lines of explanation
            sitting permanently above the composer cost more than they taught:
            in the pane's worst case it left two lines to read replies in.
            After that it lives on the mode buttons' own tooltips. */}
        {messages.length === 0 ? (
          <p className="chat-form__help">{copy.help}</p>
        ) : null}
        <div className="chat-form__row">
          <textarea
            className="field__input field__input--multiline chat-form__input"
            // Steering is often a few sentences. One row hid most of a
            // direction before it was sent, making it hard to review in the
            // middle of a session.
            aria-label={
              mode === 'steering'
                ? 'Direction for the next scene'
                : 'Question about your campaign'
            }
            rows={3}
            value={value}
            placeholder={copy.placeholder}
            disabled={disabled || sending}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              // Enter sends; Shift+Enter is a newline. A GM types these
              // one-handed mid-session.
              if (
                e.key === 'Enter' &&
                !e.shiftKey &&
                !e.nativeEvent.isComposing
              ) {
                e.preventDefault();
                void submit(e);
              }
            }}
          />
          <button
            type="submit"
            className="button button--primary chat-form__send"
            disabled={disabled || sending || !value.trim()}
            title={copy.help}
          >
            {sending ? '…' : copy.send}
          </button>
        </div>
      </form>
    </div>
  );
}
